import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ContractCard,
  EmptyState,
  FilterChips,
  LockedFeatureOverlay,
  Icon,
  Screen,
  Search,
  Text,
} from '~/atomic';
import { useAppDispatch, useAppSelector } from '~/store';
import {
  isContractSettled,
  selectContractsList,
  selectNextUnpaidByContract,
} from '~/store/contracts/selectors';
import { fetchContracts } from '~/store/contracts/thunks';

type Filter = 'all' | 'active' | 'completed' | 'high' | 'medium' | 'low';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'active', label: 'Ativos' },
  { key: 'completed', label: 'Quitados' },
  { key: 'high', label: 'Risco alto' },
  { key: 'medium', label: 'Risco médio' },
  { key: 'low', label: 'Risco baixo' },
];

export default function ContractListView() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const contracts = useAppSelector(selectContractsList);
  const nextUnpaidByContract = useAppSelector(selectNextUnpaidByContract);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (contracts.length === 0) dispatch(fetchContracts());
  }, [dispatch, contracts.length]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    const nextDueTime = (id: string) => {
      const next = nextUnpaidByContract[id];
      return next ? new Date(next.dueDate).getTime() : Infinity;
    };
    return contracts
      .filter((c) => {
        if (filter === 'active' && c.status !== 'active') return false;
        if (filter === 'completed' && c.status !== 'completed') return false;
        if (filter === 'high' && c.riskLevel !== 'high') return false;
        if (filter === 'medium' && c.riskLevel !== 'medium') return false;
        if (filter === 'low' && c.riskLevel !== 'low') return false;
        if (!term) return true;
        return (
          c.contractNumber.toLowerCase().includes(term) ||
          c.institution.name.toLowerCase().includes(term)
        );
      })
      .sort((a, b) => {
        const aPaid = isContractSettled(a);
        const bPaid = isContractSettled(b);
        if (aPaid !== bPaid) return aPaid ? 1 : -1;
        return nextDueTime(a.id) - nextDueTime(b.id);
      });
  }, [contracts, filter, search, nextUnpaidByContract]);

  return (
    <Screen headerTitle="Contratos" hasTabNavigation>
      <div className="flex flex-col gap-medium py-medium">
        <div className="flex flex-col sm:flex-row sm:items-center gap-smaller">
          <div className="flex-1 min-w-0">
            <Search
              value={search}
              onChange={setSearch}
              placeholder="Buscar por instituição ou número"
            />
          </div>
          <button
            type="button"
            onClick={() => navigate('/app/add')}
            className="h-[48px] px-medium rounded-small bg-primary-01 hover:bg-secondary-01 transition-colors shadow-level1 flex items-center justify-center gap-nano shrink-0"
          >
            <Icon name="icon-more" size={18} color="#FFFFFF" rounded={false} />
            <Text className="text-bodyLG color-intense-01">Novo contrato</Text>
          </button>
        </div>
        <LockedFeatureOverlay
          feature="contracts_advanced"
          variant="compact"
          title="Filtros avançados por risco e status"
          blurPx={3}
        >
          <FilterChips items={FILTERS} active={filter} onChange={setFilter} />
        </LockedFeatureOverlay>

        {filtered.length === 0 ? (
          <EmptyState
            icon="icon-document"
            title="Nenhum contrato encontrado"
            description={
              search
                ? 'Ajuste a busca ou os filtros.'
                : 'Comece adicionando seu primeiro contrato.'
            }
            ctaText={search ? undefined : 'Adicionar contrato'}
            onClickCta={search ? undefined : () => navigate('/app/add')}
          />
        ) : (
          <div className="flex flex-col gap-smaller">
            <Text className="text-captionLG color-intense-06 uppercase">
              {filtered.length} {filtered.length === 1 ? 'contrato' : 'contratos'}
            </Text>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-smaller">
              {filtered.map((c) => (
                <ContractCard
                  key={c.id}
                  contract={c}
                  nextInstallment={nextUnpaidByContract[c.id]}
                  onClick={() => navigate(`/app/contracts/${c.id}`)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Screen>
  );
}
